import { useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw, CheckCircle2, Edit2, Trash2 } from 'lucide-react';
import { IncomeStream } from '../types';
import { getUpcomingDates } from '../utils/schedule';
import { format, isSameDay } from 'date-fns';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

interface ExpectedIncomeProps {
  incomes: IncomeStream[];
  onEdit: (income: IncomeStream) => void;
  onDelete: (id: string) => void;
}

export default function ExpectedIncome({ incomes, onEdit, onDelete }: ExpectedIncomeProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  const handleReceiveEarly = async (incomeId: string, dateMs: number) => {
    setProcessingId(incomeId);
    try {
      const receiveIncomeEarly = httpsCallable(functions, 'receiveIncomeEarly');
      await receiveIncomeEarly({ incomeId, dateMs });
      // The snapshot listener will automatically update the data
    } catch (err) {
      console.error('Failed to receive income early:', err);
      alert('Failed to process. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  const getSummaryAmount = (inc: IncomeStream) => {
    if (!inc.schedule) return inc.amount * 12; // Fallback for legacy data
    // Basic estimation for yearly based on frequency
    let multiplier = 1;
    if (inc.schedule.frequency === 'monthly') multiplier = 12;
    if (inc.schedule.frequency === 'semi-monthly') multiplier = 24;
    if (inc.schedule.frequency === 'bi-weekly') multiplier = 26;
    if (inc.schedule.frequency === 'weekly') multiplier = 52;
    
    return inc.amount * multiplier;
  };

  if (incomes.length === 0) {
    return <p className="text-sm text-slate-500">No income streams added yet.</p>;
  }

  return (
    <div className="space-y-3">
      {incomes.map(inc => {
        const isExpanded = expandedId === inc.id;
        const upcomingDates = isExpanded && inc.schedule ? getUpcomingDates(inc.schedule, 12) : [];
        const nextDate = inc.schedule ? getUpcomingDates(inc.schedule, 1)[0] : null;

        return (
          <div key={inc.id} className="border border-emerald-100 rounded-lg bg-white overflow-hidden transition-all duration-200 shadow-sm hover:shadow-md">
            <div 
              className="flex justify-between items-center p-4 bg-emerald-50 cursor-pointer"
              onClick={() => setExpandedId(isExpanded ? null : (inc.id || null))}
            >
              <div>
                <span className="font-semibold text-emerald-900 block">{inc.source}</span>
                <span className="text-xs text-emerald-600 font-medium">
                  Est. Yearly: ${getSummaryAmount(inc).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
                <div className="flex items-center gap-2">
                  <div className="text-right mr-2">
                    <span className="block font-bold text-emerald-700">${inc.amount.toFixed(2)}</span>
                    <span className="text-xs text-emerald-600 capitalize">{inc.schedule ? inc.schedule.frequency.replace('-', ' ') : 'Legacy'}</span>
                  </div>
                  
                  <button 
                    onClick={(e) => { e.stopPropagation(); onEdit(inc); }}
                    className="text-emerald-300 hover:text-blue-500 transition-colors p-1"
                    title="Edit income"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onDelete(inc.id!); }}
                    className="text-emerald-300 hover:text-rose-500 transition-colors p-1"
                    title="Delete income"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  <div className="ml-2">
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-emerald-600" /> : <ChevronDown className="w-5 h-5 text-emerald-600" />}
                  </div>
                </div>
              </div>

            {isExpanded && (
              <div className="p-4 bg-white border-t border-emerald-100">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Upcoming Dates</h4>
                <div className="space-y-2">
                  {upcomingDates.map((date, index) => {
                    const isNext = index === 0;
                    return (
                      <div key={index} className={`flex items-center justify-between p-2 rounded-md ${isNext ? 'bg-emerald-50 border border-emerald-100' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${isNext ? 'bg-emerald-500' : 'bg-slate-300'}`}></div>
                          <span className={`text-sm ${isNext ? 'font-semibold text-emerald-900' : 'text-slate-600'}`}>
                            {format(date, 'EEEE, MMMM do, yyyy')}
                          </span>
                        </div>
                        {isNext && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReceiveEarly(inc.id!, date.getTime());
                            }}
                            disabled={processingId === inc.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-200 text-emerald-700 rounded text-xs font-semibold hover:bg-emerald-50 disabled:opacity-50 transition-colors"
                          >
                            {processingId === inc.id ? (
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            )}
                            Receive Now
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
